<template>
  <modal
    title="Ajouter une commande marché"
    effect="fade/zoom"
    cancelText="Fermer"
    okText="Enregistrer"
    v-model="isOpen"
    @ok="newMarketOrder"
    @cancel="$emit('close')"
  >
    <div slot="modal-header" class="modal-header row m-0">
      <h4 class="modal-title">Ajouter une commande marché</h4>
    </div>

    <div class="row">
      <div class="form-group has-danger col-sm-12">
        <label>Société *</label>
        <v-select
          v-model="form.merchantId"
          :options="merchantsOptions"
          @input="handleMerchantChange"
        ></v-select>
      </div>
    </div>

    <div class="row">
      <div class="form-group has-danger col-sm-12">
        <label>Concept *</label>
        <v-select
          v-model="form.concept"
          :options="conceptsOptions"
          :reduce="({ value }) => value"
        >
          <span slot="no-options"
            >Veuillez d'abord sélectionner une société</span
          >
        </v-select>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-8">
        <label>Pack</label>
        <v-select v-model="form.session" :options="sessions"></v-select>
      </div>
      <div class="form-group col-sm-4" style="padding-top: 14px">
        Pointage des participants
        <label
          class="switch switch-sm switch-text switch-info float-right mb-0 mr-0"
        >
          <input
            type="checkbox"
            class="switch-input"
            v-model="form.requiredSessionUserCount"
            :disabled="!hasPermission($store.state.user, 'ORDERS_WRITE')"
          />
          <span class="switch-label" data-on="On" data-off="Off"></span>
          <span class="switch-handle"></span>
        </label>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12">
        <label> Description commande (visible facturation) </label>

        <v-select
          style="margin-bottom: 5px"
          :options="client.orderDescriptions"
          v-model="client.billing.label"
          @input="updateBillingDescriptionForB2B"
        ></v-select>

        <textarea
          v-model="client.billing.description"
          type="text"
          class="form-control"
        />
      </div>
      <div class="form-group col-sm-12">
        <label> Label de la commande (visible coach) </label>

        <textarea v-model="form.label" type="text" class="form-control" />
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12">
        <label>Menu / Statistique *</label>
        <v-select
          v-model="form.menuStat"
          :options="menuStatsOptions"
          label="label"
          :selectable="(option) => !option.disable"
          :class="this.formErrors.menuStat ? 'has-error' : ''"
        />
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-7">
        Valeurs par défaut des sessions
        <label
          class="switch switch-sm switch-text switch-info float-right mb-0 mr-0"
        >
          <input
            type="checkbox"
            class="switch-input"
            v-model="form.editSessions"
            @input="fetchClientCoachs"
            :disabled="!hasPermission($store.state.user, 'ORDERS_WRITE')"
          />
          <span class="switch-label" data-on="On" data-off="Off"></span>
          <span class="switch-handle"></span>
        </label>
      </div>
    </div>

    <div v-if="form.editSessions" class="row">
      <div class="form-group col-sm-8">
        <label>Coach</label>

        <v-select
          v-model="form.defaultCoach"
          :options="coachs"
          @search="searchCoachs"
          :class="this.formErrors.coach ? 'has-error' : ''"
        ></v-select>
      </div>

      <div class="form-group col-sm-4">
        <label>Crédit/séance</label>

        <input type="text" class="form-control" v-model="form.defaultCredit" />
      </div>
    </div>

    <div class="row mt-1">
      <div class="form-group col-sm-12">
        Suivi de planification
        <label class="switch switch-sm switch-text switch-info ml-1 mb-0 mr-0">
          <input
            v-model="form.sessionScheduleTracking"
            type="checkbox"
            class="switch-input"
          />

          <span class="switch-label" data-on="On" data-off="Off"></span>

          <span class="switch-handle"></span>
        </label>
      </div>
    </div>

    <div v-if="!canCreateOrder && warningMessage" class="alert alert-danger">
      {{ warningMessage }}
    </div>

    <div slot="modal-footer" class="modal-footer">
      <button class="btn" @click="$emit('close')">Fermer</button>

      <button
        class="btn btn-primary"
        @click="newMarketOrder"
        :disabled="formSending || !canCreateOrder"
      >
        Enregistrer
      </button>
    </div>
  </modal>
</template>

<script>
import Modal from "vue-strap/src/Modal";
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import { COMMERCIALS_EMAILS, CONCEPT_NAMES } from "@/const";

export default {
  components: {
    Modal,
  },

  props: {
    isShow: {
      type: Boolean,
      default: false,
    },
    clientData: {
      type: Object,
      required: true,
    },
    merchantsData: {
      type: Array,
      required: true,
    },
    conceptsData: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      coachs: [],
      conceptsOptions: [],
      formSending: false,
      isOpen: false,
      sessions: [],
      client: null,

      form: {
        client: null,
        defaultCoach: null,
        defaultCredit: "30",
        description: null,
        label: null,
        menuStat: null,
        requiredSessionUserCount: null,
        session: null,
        merchantId: null,
        concept: null,
        sessionScheduleTracking: false,
      },

      formErrors: {
        client: null,
        menuStat: null,
        session: null,
      },
    };
  },

  computed: {
    ...mapGetters({ menuStatsOptions: "getMenuStatsOptions" }),

    cleanedCredit() {
      return (
        parseFloat(this.form.defaultCredit.replace(",", ".")).toFixed(2) * 100
      );
    },

    marketMerchantsConfig() {
      return [
        {
          id: this.$store.state.merchantIds.corporate,
          conceptName: CONCEPT_NAMES.collectivity,
        },
        {
          id: this.$store.state.merchantIds.educapa,
          conceptName: CONCEPT_NAMES.educapa,
        },
      ];
    },

    defaultMerchant() {
      const defaultMerchant = this.merchantsData.find(
        (m) => m.id === this.$store.state.merchantIds.corporate,
      );

      return defaultMerchant
        ? {
            label: defaultMerchant.name,
            value: defaultMerchant.id,
          }
        : null;
    },

    merchantsOptions() {
      const allowedIds = this.marketMerchantsConfig.map((c) => c.id);

      return this.merchantsData
        .filter((m) => allowedIds.includes(m.id))
        .map((m) => ({
          label: m.name,
          value: m.id,
        }));
    },

    canCreateOrder() {
      return (
        this.merchantsOptions.length > 0 && this.conceptsOptions.length > 0
      );
    },

    warningMessage() {
      if (this.merchantsOptions.length === 0) {
        return "Aucun merchant disponible pour les commandes marché";
      }

      if (this.conceptsData.length === 0) {
        return "Aucun concept disponible pour les commandes marché";
      }

      return null;
    },
  },

  watch: {
    isShow(val) {
      this.isOpen = val;
    },

    clientData(val) {
      this.client = val;
    },
  },

  created() {
    this.isOpen = this.isShow;
    this.client = this.clientData;
    this.form.merchantId = this.defaultMerchant;
    this.handleMerchantChange(this.defaultMerchant);

    this.form.menuStat = this.menuStatsOptions.find(
      (m) => m.value === "pack-fidelisation",
    );

    for (let i = 1; i <= 200; i++) {
      this.sessions.push({
        label: this.formatPack(i.toString()),
        value: i.toString(),
      });

      if (!this.form.session) {
        this.form.session = this.sessions[0];
      }
    }
  },

  methods: {
    updateBillingDescriptionForB2B(value) {
      this.client.billing = {
        ...this.client.billing,
        description: value ? value.description : "",
        label: value ? value.label : "",
      };
    },

    checkFields() {
      this.formErrors.client = !this.form.client;
      this.formErrors.menuStat = !this.form.menuStat;
      this.formErrors.session = !this.form.session;

      return (
        !this.formErrors.menuStat &&
        !this.formErrors.session &&
        !this.formErrors.client
      );
    },

    async fetchClientCoachs() {
      if (this.form.client) {
        try {
          const { data } = await this.$api.get(
            `/clients/coachs/${this.form.client.value}`,
          );

          this.coachs = data;
        } catch (e) {
          console.log(e);
        }
      }
    },

    async searchCoachs(search, loading) {
      if (search !== "") {
        await this.fetchCoachs(search, loading, this);
      }
    },

    fetchCoachs: debounce(async (search, loading, vm) => {
      if (!search || search.length < 3) {
        return;
      }

      loading(true);

      const { data } = await vm.$api.post("/users/coachs/search", {
        query: search,
        filterBy: ["firstName", "lastName", "email"],
      });

      vm.coachs = data.map((c) => ({
        label: `${c.coach ? c.coach.name : ""} (${c.email})`,
        value: c.email,
        id: c.id,
      }));

      loading(false);

      return vm.clients;
    }, 300),

    handleMerchantChange(merchant) {
      if (!merchant?.value) {
        this.form.concept = null;
        this.conceptsOptions = [];

        return;
      }

      const config = this.marketMerchantsConfig.find(
        (c) => c.id === merchant.value,
      );

      if (config) {
        this.form.concept = config.conceptName;

        this.conceptsOptions = this.conceptsData
          .filter((c) => c.name === config.conceptName)
          .map((c) => ({
            label: c.name,
            value: c.id,
          }));
      } else {
        this.form.concept = null;
        this.conceptsOptions = [];
      }
    },

    async newMarketOrder() {
      this.form.client = this.client.email;
      this.form.description = this.client.billing.description;

      if (!this.checkFields() || this.formSending) {
        this.$errorToast("Les informations du client sont manquantes");

        return;
      }

      const isConversion =
        this.form.menuStat &&
        this.form.menuStat.value &&
        (this.form.menuStat.value === "decouverte" ||
          this.form.menuStat.value === "one-shot");

      const isAcquisition =
        this.form.menuStat &&
        this.form.menuStat.value &&
        (this.form.menuStat.value === "decouverte" ||
          this.form.menuStat.value === "pack-conversion" ||
          this.form.menuStat.value === "one-shot" ||
          this.form.menuStat.value === "autres");

      const isFidelisation =
        this.form.menuStat &&
        this.form.menuStat.value &&
        (this.form.menuStat.value === "pack-conversion" ||
          this.form.menuStat.value === "pack-fidelisation" ||
          this.form.menuStat.value === "one-shot");

      let defaultCoach;
      let defaultCredit;

      if (this.form.editSessions) {
        defaultCoach = this.form.defaultCoach && this.form.defaultCoach.value;
        defaultCredit = this.cleanedCredit;
      }

      try {
        this.$emit("orderLoading", true);
        this.formSending = true;

        const { data } = await this.$api.post("/orders", {
          client: this.form.client,
          commercial: COMMERCIALS_EMAILS.marketEstablishment,
          concept: this.form.concept,
          defaultCoach,
          defaultCredit,
          isAcquisition,
          isConversion,
          isFidelisation,
          isOneShotEvent: this.form.menuStat?.value === "one-shot",
          labelDescription: this.client.billing.label,
          description: this.form.description,
          label: this.form.label,
          merchantId: this.form.merchantId.value,
          price: "0",
          requiredSessionUserCount: this.form.requiredSessionUserCount,
          session: this.form.session.value,
          sessionScheduleTracking: this.form.sessionScheduleTracking,
        });

        this.$emit("success", data);
        this.$emit("close");
      } catch (err) {
        this.formSending = false;

        this.$errorToast(
          err.response?.data?.message ||
            "Une erreur est survenue lors de la création de la commande.",
        );
      } finally {
        this.$emit("orderLoading", false);
      }
    },
  },
};
</script>
